import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Circle, CircleDot, Download } from "lucide-react";
import type { DeviceInfo } from "../types";
import { ApiError, listDevices } from "../lib/api";
import { useServerBase } from "../lib/settings";
import { SimulatorManager } from "../components/SimulatorManager";

const DOWNLOAD_URL = "https://mobai.run/download";

// Go's map iteration is randomised, so `/api/v1/devices` returns its list
// in a different order on every poll. Apply a stable client-side sort so
// the list doesn't visibly reshuffle every 3 seconds.
function sortDevices(list: DeviceInfo[]): DeviceInfo[] {
  return [...list].sort((a, b) => {
    // Running bridges first, then real devices before simulators, then by
    // display name, finally by id as the tie-breaker.
    if (a.bridgeRunning !== b.bridgeRunning) return a.bridgeRunning ? -1 : 1;
    if (a.virtual !== b.virtual) return a.virtual ? 1 : -1;
    const an = (a.name || a.id).toLowerCase();
    const bn = (b.name || b.id).toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

function platformIcon(p: string, virtual: boolean) {
  if (p === "ios") return virtual ? "🖥️" : "📱";
  if (p === "android") return virtual ? "🤖" : "📱";
  return "❓";
}

export function DeviceList() {
  const [serverBase] = useServerBase();
  const [devices, setDevices] = useState<DeviceInfo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const sortedDevices = useMemo(
    () => (devices ? sortDevices(devices) : null),
    [devices],
  );

  useEffect(() => {
    // Use a per-effect cancel flag so Strict Mode's double-mount doesn't
    // discard the first fetch while blocking the second on a shared ref.
    let cancelled = false;

    const tick = async () => {
      try {
        const list = await listDevices();
        if (cancelled) return;
        setDevices(list);
        setError(null);
        setUpdatedAt(Date.now());
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof ApiError ? `${e.status} ${e.message}` : String(e);
        setError(msg);
      }
    };

    tick();
    const id = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [serverBase]);

  // First load failed and we have nothing to show: server probably isn't running.
  if (error && !devices) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-lg font-semibold mb-2">Can't reach MobAI</h1>
        <p className="text-sm text-surface-400 mb-1">
          Start the MobAI desktop app, then reload.
        </p>
        <p className="text-xs text-surface-500 mb-6">
          Trying <code className="text-surface-300">{serverBase}</code>
        </p>
        <div className="flex items-center justify-center gap-2">
          <a
            href={DOWNLOAD_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan/20 text-accent-cyan border border-accent-cyan/40 text-sm hover:bg-accent-cyan/30"
          >
            <Download className="w-4 h-4" />
            Download MobAI
          </a>
          <Link
            to="/settings"
            className="inline-block px-4 py-2 rounded-md border border-surface-600 text-sm text-surface-300 hover:bg-surface-800"
          >
            Change server URL
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="flex items-baseline gap-3 mb-4">
        <h1 className="text-lg font-semibold">Devices</h1>
        {devices && (
          <span className="text-xs text-surface-500">
            {devices.length} {devices.length === 1 ? "device" : "devices"}
            {updatedAt ? ` · updated ${new Date(updatedAt).toLocaleTimeString()}` : ""}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md border border-rose-500/30 bg-rose-500/10 text-sm text-rose-300">
          Failed to load devices: {error}
        </div>
      )}

      {devices && devices.length === 0 && !error && (
        <div className="text-sm text-surface-500 italic">No devices connected.</div>
      )}

      <div className="space-y-2">
        {sortedDevices?.map((d) => (
          <Link
            key={d.id}
            to={`/device/${encodeURIComponent(d.id)}`}
            className="flex items-center gap-3 p-3 rounded-lg border border-surface-800 hover:border-accent-cyan/50 hover:bg-surface-900 transition-colors"
          >
            <div className="text-xl" aria-hidden>
              {platformIcon(d.platform, d.virtual)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-surface-100">
                {d.name || d.id}
                {d.virtual && (
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300 align-middle">
                    {d.platform === "ios" ? "Simulator" : "Emulator"}
                  </span>
                )}
              </div>
              <div className="text-xs text-surface-500 truncate">
                {[
                  d.platform,
                  d.osVersion,
                  d.model,
                  d.screenWidth && d.screenHeight ? `${d.screenWidth}×${d.screenHeight}` : null,
                  d.id,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-surface-400">
              {d.bridgeRunning ? (
                <CircleDot className="w-3.5 h-3.5 text-emerald-400" />
              ) : (
                <Circle className="w-3.5 h-3.5 text-surface-600" />
              )}
              {d.bridgeRunning ? "bridge up" : "bridge stopped"}
            </div>
          </Link>
        ))}
      </div>

      <SimulatorManager />
    </div>
  );
}
